import {
  AfterContentInit, AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  inject, input, Input,
  model, OnDestroy,
  OnInit,
  signal, TemplateRef,
  Type,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import * as uuid from 'uuid';
import {LabelOptions} from '@core/classes/options/label-options';
import {InputModes, InputTypes} from '@core/enums';
import {NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle} from '@ng-bootstrap/ng-bootstrap';
import {InputComponent, InputOptions} from '@shared/components';
import {FILTER_ARC_NUMBERS, FILTER_CIC_NUMBERS} from '@core/constants';
import {APIOptions} from '@core/classes/api-options';
import {AnyTableClass} from '@core/types';
import {TranslateService} from '@ngx-translate/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {NgComponentOutlet, NgTemplateOutlet} from '@angular/common';

export class DropdownOptions<T> {
  id?: string = uuid.v4();
  dataId?: string;
  label?: LabelOptions;
  placeholder?: string;
  name?: string;
  itemTemplate?: TemplateRef<any>;
  type?: InputTypes = InputTypes.text;
  value?: T;
  api?: APIOptions<T> | {[key: string]: APIOptions<T>};
  data?: T[];
  initialData?: T[];
  interface?: Type<T>; //new (...args: any[]) => Tw
  initialValue?: any;
  formControl?: FormControl<any>;
  formGroup?: FormGroup;
  formControlName?: string;
  required?: boolean | true;
  searchComponent?: Type<any> | null;
  onSelect?: (value?: T, index?: number) => void;
  onClear?: (clearMedication?: boolean) => void;
  descriptionKey?: string;
  disabled?: boolean;
  isLoading?: boolean;

  constructor(args?: DropdownOptions<T>) {
    this.id = args?.id || uuid.v4();
    this.dataId = args?.dataId || 'coding_id';
    this.label = args?.label;
    this.placeholder = args?.placeholder || '';
    this.name = args?.name || '';
    this.itemTemplate = args?.itemTemplate;
    this.type = args?.type || InputTypes.text;
    this.value = args?.value;
    this.api = args?.api;
    this.data = args?.data;
    this.initialData = args?.initialData;
    this.interface = args?.interface;
    this.initialValue = args?.initialValue;
    this.formControl = args?.formControl;
    this.formGroup = args?.formGroup;
    this.formControlName = args?.formControlName;
    this.required = args?.required !== undefined ? args?.required : false;
    this.searchComponent = args?.searchComponent;
    this.onSelect = args?.onSelect;
    this.onClear = args?.onClear;
    this.descriptionKey = args?.descriptionKey || 'description';
    this.disabled = args?.disabled !== undefined ? args?.disabled : false;
    this.isLoading = args?.isLoading && true;
  }
}

@UntilDestroy()
@Component({
  selector: 'ncp-dropdown',
  imports: [
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    NgComponentOutlet,
    NgTemplateOutlet,
    ReactiveFormsModule
  ],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
})
export class DropdownComponent<T extends AnyTableClass> implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('ddEl') public el?: NgbDropdown;
  @ViewChild('hiddenEl') public hidden!: ElementRef<HTMLInputElement>;
  @ViewChild('mainBtn') public mainBtn?: ElementRef<HTMLButtonElement>;
  private readonly translate = inject(TranslateService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected translations = this.translate.instant('dropdown');
  protected invalidMessage: string = '';
  protected _data: WritableSignal<T[]> = model([]);
  protected _filteredData: WritableSignal<T[]> = model([]);
  protected _searchTerms: {[id: string]: string} = {};
  protected classChanges?: MutationObserver;
  protected extraClasses?: string = '';

  itemTemplate = input<TemplateRef<any>>();
  options = model<DropdownOptions<T>>(new DropdownOptions());
  selectedItem = model<{item?: T, index?: number}>({});

  dropDownEffect = effect(() => {
    if (this.options().data) {
      this.data = this.options().data || [];
    }

    let ddData = JSON.parse(localStorage.getItem('dropDownData') || '{}');
    if(ddData && this.options().name && ddData.hasOwnProperty(this.options().name!)) {
      this.data = ddData[this.options().name!];
    }
  });

  set searchTerms(value: {key: string, term: string}) {
    this._searchTerms[value.key] = value.term
  };

  get searchTerms():  {[id: string]: string} {
    return this._searchTerms;
  }

  set data(data: T[]) {
    this._data.set(data);
  }

  get data() {
    return this._data();
  }

  set name(name: string) {
    this.options().name = name;
    this.options().id = name;

    let ddData = JSON.parse(localStorage.getItem('dropDownData') || '{}');
    if(ddData && this.options().name && ddData.hasOwnProperty(this.options().name!)) {
      this.data = ddData[this.options().name!];
    }
  }

  set filteredData(data: T[]) {
    this._filteredData.set(data)
  }

  get filteredData() {
    return this._filteredData();
  }

  ngOnInit() {
    this.reset();

    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('dropdown');
    });
  }

  ngAfterViewInit(): void {
    //console.log(this.hidden?.nativeElement);
    this.classChanges = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation: MutationRecord) => {
        // this is called twice because the old class is removed and the new added
        this.extraClasses = this.hidden?.nativeElement.classList.toString();
        this.detectChanges();
        //console.log(this.extraClasses);
        // console.log(
        //   `${mutation.attributeName} changed to ${this.hidden?.nativeElement.classList}`
        // );
      });
    });

    if(this.hidden) {
      this.classChanges.observe(this.hidden?.nativeElement, {
        attributeFilter: ['class'],
      });
    }
  }

  ngOnDestroy() {
    this.dropDownEffect.destroy();
    this.classChanges?.disconnect()
  }

  detectChanges = () => {
    this.changeDetectorRef.detectChanges();
  }

  onSelect = (data: T, index: number) => {
    this.selectedItem.set({item: data, index: index});
    this.options().value = data;
    if(this.options().formControl && this.options().dataId) {
      this.options().formControl!.setValue(data[this.options().dataId!]);
    }
    this.options().onSelect?.(data, index);
  }

  onClick = (event: MouseEvent) => {
    this.options()?.formControl?.markAsTouched();
  }

  onSearch = (id: string, term: string) => {
    console.log(id + '  ' + term);
    this.searchTerms = {key: id, term};
  }

  enable() {
    if(this.options().formControl) {
      this.options().formControl?.enable();
    }
  }

  disable() {
    if(this.options().formControl) {
      this.options().formControl?.disable();
    }
  }

  reset() {
    if(this.options().disabled) {
      this.disable();
    }
    this.clearSelection();
  }

  clearSelection = () => {
    this.selectedItem.set({});
    this.options().value = undefined;
    if(this.options().formControl) {
      this.options().formControl!.setValue(undefined);
    }
    if(this.options().onSelect) {
      this.options().onSelect?.();
    }

    this.extraClasses = '';

    this.options()?.onClear?.(false);
  }

  protected readonly Object = Object;
}
